import { useMutation, useQueryClient } from '@tanstack/react-query'
import DOMPurify from 'dompurify'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useParams } from 'react-router'

import { Button } from '@/components/elements/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/elements/card'
import { dismissWelcome } from '@/services/dashboard.service'

function WelcomeSection({ welcome }) {
  const { entityId } = useParams()
  const queryClient = useQueryClient()

  // Local state for "Remind me later" (session-only hide)
  const [hiddenForSession, setHiddenForSession] = useState(false)

  const dismissMutation = useMutation({
    mutationFn: () => dismissWelcome(entityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', entityId] })
    },
  })

  // Don't render if no welcome content or already dismissed/hidden
  const hasContent = welcome.message || welcome.videoUrl
  if (!hasContent || welcome.hasDismissed || hiddenForSession) {
    return null
  }

  const handleDismiss = () => {
    dismissMutation.mutate()
  }

  const handleRemindLater = () => {
    setHiddenForSession(true)
  }

  return (
    <Card className="border-primary/20 bg-primary/5 relative">
      <CardHeader>
        <CardTitle>Welcome</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {welcome.videoUrl && (
          <div className="aspect-video w-full max-w-2xl overflow-hidden rounded-lg">
            <video
              src={welcome.videoUrl}
              controls
              className="h-full w-full object-cover"
              preload="metadata"
            >
              <track kind="captions" />
            </video>
          </div>
        )}

        {welcome.message && (
          <div
            className="prose prose-sm text-foreground max-w-none"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(welcome.message) }}
          />
        )}

        <div className="flex gap-2 pt-2">
          <Button onClick={handleDismiss} loading={dismissMutation.isPending}>
            Got it
          </Button>
          <Button variant="outline" onClick={handleRemindLater}>
            Remind me later
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

WelcomeSection.propTypes = {
  welcome: PropTypes.shape({
    message: PropTypes.string,
    videoUrl: PropTypes.string,
    hasDismissed: PropTypes.bool,
  }).isRequired,
}

export default WelcomeSection
